/**
 * Token storage that adapts to the runtime WITHOUT any @capacitor/* dependency (so it compiles and
 * ships in the web build before Capacitor is installed — Faza 1 of docs/mobile/CAPACITOR_PLAN.md):
 *
 *  - WEB (browser): `localStorage` — shared by every tab of the same origin and it survives a
 *    browser restart.
 *  - NATIVE (Capacitor WebView): `localStorage` — persists across app restarts (sessionStorage is
 *    wiped when the OS kills the app), and is sandboxed to our packaged app (no third-party JS can
 *    read it). This is what makes the mobile session survive a cold start.
 *
 * WHY WEB MOVED OFF sessionStorage: sessionStorage is per-TAB. Opening any link in a new tab
 * (middle-click, Ctrl+click, "duplicate tab", a bookmark) started that tab with an EMPTY store, so
 * the refresh token was missing, the guard found no session and bounced a perfectly logged-in user
 * to /login. The old scoping bought very little: an XSS runs INSIDE the tab and reads sessionStorage
 * just as easily — the only thing it really protected was "session dies when the tab closes", which
 * is what made the app unusable across tabs. Logout/expiry still clear the token in every tab (see
 * `AuthService` — it listens for the cross-tab `storage` event), and refresh tokens still rotate
 * server-side with family revocation.
 *
 * Native detection uses the global `window.Capacitor` object that Capacitor injects into native
 * WebViews — no import, so this file has zero build-time coupling to Capacitor.
 *
 * All methods are synchronous (both Storage backends are sync), so the auth flow stays synchronous.
 *
 * TODO (Faza 4 hardening, once Capacitor is installed): swap the native backend to
 * `@capacitor/preferences` or secure storage (iOS Keychain / Android Keystore) for encryption-at-rest,
 * with a one-time migration from localStorage.
 */

interface CapacitorGlobal {
	isNativePlatform?: () => boolean;
}

function isNative(): boolean {
	const cap = (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor;
	return !!cap?.isNativePlatform?.();
}

function backend(): Storage {
	void isNative(); // both runtimes use localStorage today; kept for the Faza 4 secure-storage swap
	return localStorage;
}

/**
 * One-time hand-over of a session that was written by an older build into `sessionStorage`.
 * Without it every tab that is open across the deploy would be logged out on its next reload.
 */
function migrateFromSessionStorage(key: string): string | null {
	try {
		const legacy = sessionStorage.getItem(key);

		if (legacy !== null) {
			localStorage.setItem(key, legacy);
			sessionStorage.removeItem(key);
			return legacy;
		}
	} catch {
		/* sessionStorage unavailable (private mode / disabled) — nothing to migrate */
	}

	return null;
}

export const TokenStorage = {
	get(key: string): string | null {
		return backend().getItem(key) ?? migrateFromSessionStorage(key);
	},
	set(key: string, value: string): void {
		backend().setItem(key, value);
	},
	remove(key: string): void {
		backend().removeItem(key);

		try {
			sessionStorage.removeItem(key);
		} catch {
			/* sessionStorage unavailable — nothing to clear */
		}
	},
};
